<script setup>
import { onMounted, onBeforeMount, ref, watch, computed, onUnmounted } from 'vue';

import { useRoute, useRouter } from 'vue-router';

import { storeToRefs } from 'pinia';

import { useThemeConfig } from '@core/composable/useThemeConfig';
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';

import { useToast } from 'primevue/usetoast';

import {
  AllEnterpriseModule,
  LicenseManager,
  IntegratedChartsModule,
  SparklinesModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from 'ag-grid-enterprise';

import Toast from '@/@core/components/Toast.vue';
import HardGateDialog from '@/components/billing/HardGateDialog.vue';
import EbayListingDialogHost from '@/components/ebay/listings/EbayListingDialogHost.vue';
import MaintenanceOverlay from '@/components/ui/MaintenanceOverlay.vue';
import NavigationProgressBar from '@/components/ui/NavigationProgressBar.vue';
import { useChatwoot } from '@/composables/useChatwoot.js';
import { useMutationReviewBadgeLifecycle } from '@/composables/useMutationReviewBadgeLifecycle';
import { useVersionCheck } from '@/composables/useVersionCheck.js';
import { realtimeSocketEnabled } from '@/config/realtime';
import { TOOLTIP_SHOW_DELAY_MS } from '@/config/tooltip.js';
import { getQueryParamInviteCode } from '@/helpers/helpers.js';
import i18n from '@/plugins/i18n';
import { useAccountStore } from '@/stores/account.js';
import { useAlertsStore } from '@/stores/alerts.js';
import { useAppVersionStore } from '@/stores/appVersion';
import { useAuthStore } from '@/stores/auth';
import { useBlurStore } from '@/stores/blur';
import { useConnectionAlertsStore } from '@/stores/connectionAlerts';
import { useEnvironmentStore } from '@/stores/environment';
import { useLanguageStore } from '@/stores/language.js';
import { useMaintenanceStore } from '@/stores/maintenance';
import { useNotificationStore } from '@/stores/notifications';
import { useThemeStore } from '@/stores/theme';
import { useViewportStore } from '@/stores/viewport';

import VersionUpdateDialog from './components/ui/dialogs/VersionUpdateDialog.vue';
import VersionUpdateNotice from './components/ui/dialogs/VersionUpdateNotice.vue';
import { useLoaderStore } from './stores/loader';

// import {
//   ModuleRegistry,
//   provideGlobalGridOptions
//   //  AllCommunityModule // this included in ag-grid-enterprise
// } from 'ag-grid-community'

// Set up AG Grid Enterprise license
LicenseManager.setLicenseKey(
  'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-101270}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Crystal_Logistics_Corporation}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{WisePPC}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{WisePPC}_need_to_be_licensed___{WisePPC}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{5_May_2027}____[v3]_[0102]_MTgwOTQ3MTYwMDAwMA==99121b36db25180f85ac685231f72216',
);

// Register all Community features
// ModuleRegistry.registerModules([AllCommunityModule]) // this included in ag-grid-enterprise
// Register all enterprise features
// ModuleRegistry.registerModules([AllEnterpriseModule])
// Register all charts features
ModuleRegistry.registerModules([
  AllEnterpriseModule,
  IntegratedChartsModule.with(AgChartsEnterpriseModule),
  SparklinesModule.with(AgChartsEnterpriseModule),
]);

// Mark all grids as using legacy themes
// One tooltip show delay for every grid: header / cell tooltips match v-wp-tooltip. The switch
// delay too, or AG Grid shows the next tooltip after 200 ms once one has been shown.
provideGlobalGridOptions({
  theme: 'legacy',
  tooltipShowDelay: TOOLTIP_SHOW_DELAY_MS,
  tooltipSwitchShowDelay: TOOLTIP_SHOW_DELAY_MS,
});

const account = useAccountStore();
const blur = useBlurStore();
const environmentStore = useEnvironmentStore();
const loaderStore = useLoaderStore();
const route = useRoute();
const router = useRouter();
const loggedIn = computed(() => useAuthStore()?.loggedIn);
const isProduction = computed(() => environmentStore.isProduction);
const isDevelopment = computed(() => environmentStore.isDevelopment);
const theme = useThemeStore();
const toast = useToast();
const language = useLanguageStore();
const alertsStore = useAlertsStore();
const authStore = useAuthStore();
useMutationReviewBadgeLifecycle();
const accountStore = useAccountStore();
const notificationStore = useNotificationStore();
// Dev always opens the socket (and the inbox). Other envs open the socket only
// when VITE_ENABLE_REALTIME_SOCKET is on, and never the inbox.
const realtimeSocket = computed(() => isDevelopment.value || realtimeSocketEnabled);

async function syncRealtimeSocket(isLoggedIn) {
  if (!realtimeSocket.value) return;
  if (!isLoggedIn) {
    notificationStore.disconnectWebSocket();
    return;
  }
  if (isDevelopment.value) {
    await notificationStore.initialize();
    notificationStore.initializeWebSocket({ inbox: true });
    return;
  }
  notificationStore.initializeWebSocket({ inbox: false });
}
const connectionAlertsStore = useConnectionAlertsStore();
const loading = computed(() => loaderStore.hasShownLoader);

const {
  initializeChatwoot,
  setupWatchers: setupChatwootWatchers,
} = useChatwoot();

const maintenanceStore = useMaintenanceStore();
const versionUpdateDialogRef = ref(null);
const { isNewVersionAvailable, clearCacheAndReload, dismissUpdate } = useVersionCheck();
const { isBreaking, requiresReload } = storeToRefs(useAppVersionStore());

// A breaking build opens the blocking dialog; a non-breaking one only shows the notice below.
watch(requiresReload, (newValue) => {
  if (newValue && !maintenanceStore.isMaintenanceActive && versionUpdateDialogRef.value) {
    versionUpdateDialogRef.value.show('version');
  }
});

// Show the same VersionUpdateDialog on chunk load errors (e.g. after deploy)
const handleChunkLoadError = () => {
  if (versionUpdateDialogRef.value) {
    versionUpdateDialogRef.value.show('chunk');
  }
};

window.addEventListener('chunkLoadError', handleChunkLoadError);

const handleVersionReload = () => {
  clearCacheAndReload();
};

const handleVersionDismiss = () => {
  dismissUpdate();
  window.dispatchEvent(new CustomEvent('chunkLoadErrorDismissed'));
};

const {
  syncInitialLoaderTheme,
  syncVuetifyThemeWithTheme: syncConfigThemeWithVuetifyTheme,
  handleSkinChanges,
} = useThemeConfig();

const handleToastClose = () => {
  alertsStore.clearErrors();
};

watch(
  () => alertsStore.errors,
  (value) => {
    if (value) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: alertsStore.errors,
        group: 'errors',
        life: 8000,
      });
    }
  }, { immediate: true },
);

// Initialize or disconnect WebSocket based on login state
watch(
  () => loggedIn.value,
  async (isLoggedIn) => {
    const inviteCode = getQueryParamInviteCode('inviteCode');
    // Only fetch profiles for non-login scenarios (e.g., page refresh while logged in)
    // Login page now handles initial token fetch to avoid race condition
    const isRegistrationRoute = route.name?.startsWith('register');
    if ((inviteCode === undefined || inviteCode === null) && !isRegistrationRoute) {
      if (isLoggedIn && route.name !== 'login') {
        await account.syncBusinessProfiles();
      }
    }

    // Server-side preferences need a valid auth token, so dev loads them only
    // after the user is authenticated. Failure is non-blocking — the drawer
    // falls back to defaultPreferences / localStorage.
    await syncRealtimeSocket(isLoggedIn);
  },
);

// Ensure access token is available when business profiles change
watch(
  () => account.businessProfiles,
  async () => {
    if (loggedIn.value) {
      if (account.businessProfiles && account.businessProfiles.length > 0) {
        const businessProfileId = account.currentBusinessProfile?.id;
        if (businessProfileId && !authStore.getAppToken(businessProfileId)) {
          await authStore.getAppAccessToken(businessProfileId);
        }
      }
    }
  },
);

// Server-side user settings need a per-BP app token. Load them once the token is available
// (and reload on BP switch). The blur/notifications stores dedup by token, so the eager calls
// in onBeforeMount/onMounted/loggedIn-watcher stay gated no-ops until the token is ready here.
watch(
  () => authStore.accessToken,
  (token) => {
    if (!token) return;
    blur.initializeBlur();
    language.initializeLanguageFromServer();
    if (isDevelopment.value) notificationStore.initialize();
  },
  { immediate: true },
);

onBeforeMount(async () => {
  theme.initializeTheme();
  await language.initializeLanguage();
  blur.initializeBlur();

  // Initialize Chatwoot SDK and set up reactive watchers
  initializeChatwoot();
  setupChatwootWatchers();

  const host = window.location.hostname;
  if (host === 'dev.wiseppc.com' || host === 'stage.wiseppc.com') {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
  }
});

const hideLoader = () => {
  if (loaderStore.hasShownLoader) {
    setTimeout(() => {
      loaderStore.hideLoader();
    }, 0);
  }
};

onMounted(async () => {
  const queryParams = new URLSearchParams(window.location.search);

  // Check for OAuth errors in URL
  const oauthError = queryParams.get('error');
  const errorDescription = queryParams.get('error_description');

  // /oauth-landing owns its own OAuth error state ("could not complete sign-in" + back to login);
  // bouncing it to /login here hid that message behind a preloader nobody ever dismissed.
  const isOAuthLanding = window.location.pathname.startsWith('/oauth-landing');

  if (oauthError && !isOAuthLanding) {
    // Show error using alertsStore (Element Plus)
    // The query is attacker-controllable: a signed-in user only gets fixed text (connection returns
    // such as the Amazon Ads one carry ?error= too and show their own message on their page).
    const errorMessage = loggedIn.value
      ? oauthError === 'access_denied'
        ? i18n.global.t('Sign-in was cancelled.')
        : i18n.global.t('Sign-in failed. Please try again.')
      : errorDescription || `OAuth error: ${oauthError}`;
    alertsStore.setErrors(errorMessage, false);

    // Clear error params from URL
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('error');
    cleanUrl.searchParams.delete('error_description');

    // If user is not logged in, redirect to login
    if (!loggedIn.value) {
      // Returning early skips the hideLoader() at the end of this hook — drop it here, or the
      // bootstrap preloader stays up over the login page forever.
      hideLoader();
      await router.push({ name: 'login' });
      return;
    } else {
      window.history.replaceState({}, '', cleanUrl.toString());
    }
  }

  // Add listener for showing toasts from stores
  window.addEventListener('showToast', (event) => {
    const { severity, summary, detail, life, group } = event.detail;
    toast.add({
      severity: severity || 'info',
      summary: summary || 'Notification',
      detail: detail,
      group: group || 'errors',
      life: life || 5000,
    });
  });

  // Set viewport based on current route (+ orientation-change re-apply)
  useViewportStore().initViewport(() => route.path);

  // Hard refresh: the loggedIn watcher above doesn't fire, so open the socket here.
  // initialize() pulls drawer preferences and stays dev-only (inside syncRealtimeSocket).
  await syncRealtimeSocket(loggedIn.value);

  // Chatwoot & LogRocket for already logged-in users
  // Note: updateAuthenticatedContext() is NOT called here directly.
  // On reload, loggedIn is already true so the loggedIn watcher won't fire.
  // Instead, we rely on the currentBusinessProfile/currentMarketplace watchers
  // in setupChatwootWatchers() which fire when the stores populate after syncBusinessProfiles().
  if (loggedIn.value) {
    if (isProduction.value) {
      await authStore.identifyLogRocketUser();
    }
  } else if (!isOAuthLanding) {
    // /oauth-landing owns the preloader through the code exchange and drops it itself. It mounts
    // only after the router guard and its lazy chunk, so hiding here showed a blank page between
    // the bootstrap dots and the landing's own.
    hideLoader();
  }
});

onUnmounted(() => {
  if (realtimeSocket.value) notificationStore.disconnectWebSocket();
});

syncInitialLoaderTheme();
syncConfigThemeWithVuetifyTheme();
handleSkinChanges();

// Fetch connection alerts when business profile is loaded/changed
watch(
  () => accountStore.currentBusinessProfile,
  (profile) => {
    if (profile?.id && loggedIn.value) {
      connectionAlertsStore.fetchNotifications();
    }
  },
  { immediate: true },
);

</script>

<template>
  <ElConfigProvider :locale="language.elementPlusLocale">
    <NavigationProgressBar />
    <Preloader v-if="loading" />
    <VApp>
      <RouterView />
    </VApp>
    <Toast
      position="top-center"
      group="errors"
      @close="handleToastClose"
      @life-end="handleToastClose"
    />
    <VersionUpdateDialog
      ref="versionUpdateDialogRef"
      @reload="handleVersionReload"
      @dismiss="handleVersionDismiss"
    />
    <EbayListingDialogHost />
    <VersionUpdateNotice
      v-if="isNewVersionAvailable && !isBreaking && !maintenanceStore.isMaintenanceActive"
      @reload="handleVersionReload"
      @dismiss="handleVersionDismiss"
    />
    <HardGateDialog />
    <MaintenanceOverlay />
  </ElConfigProvider>
</template>

<style scoped>
header {
  line-height: 1.5;
  max-height: 100vh;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

nav {
  width: 100%;
  font-size: 12px;
  text-align: center;
  margin-top: 2rem;
}

nav a.router-link-exact-active {
  color: var(--color-text);
}

nav a.router-link-exact-active:hover {
  background-color: transparent;
}

nav a {
  display: inline-block;
  padding: 0 1rem;
  border-left: 1px solid var(--color-border);
}

nav a:first-of-type {
  border: 0;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
    padding-right: calc(var(--section-gap) / 2);
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }

  nav {
    text-align: left;
    margin-left: -1rem;
    font-size: 1rem;

    padding: 1rem 0;
    margin-top: 1rem;
  }
}
</style>
